import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import OrderModal from "@/components/OrderModal";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Products", path: "/products" },
  { label: "Zumo Points", path: "/zumo-points" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav shadow-sm" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-36 pt-4">
        {/* Logo */}
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_zumo-fresh-juice/artifacts/b5i6fq4z_WhatsApp%20Image%202026-04-15%20at%205.21.42%20PM.jpeg"
            alt="The Zumo Company"
            className="h-[120px] w-[120px] rounded-full object-cover"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={`text-sm uppercase tracking-[0.15em] font-semibold transition-colors duration-300 ${
                location.pathname === link.path
                  ? "text-[#283618]"
                  : "text-[#6F5E58] hover:text-[#283618]"
              }`}
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              {link.label}
            </Link>
          ))}
          <Button
              data-testid="nav-order-btn"
              onClick={() => setOrderOpen(true)}
              className="rounded-full px-7 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all duration-300 hover:scale-105"
              style={{
                background: "#283618",
                color: "#FCF8F6",
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              Order Now
            </Button>
        </div>

        {/* Mobile Toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? (
            <X size={24} color="#283618" />
          ) : (
            <Menu size={24} color="#283618" />
          )}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden mobile-menu-enter glass-nav px-6 pb-6 pt-2"
        >
          <div className="flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className={`text-sm uppercase tracking-[0.15em] font-semibold py-2 transition-colors ${
                  location.pathname === link.path
                    ? "text-[#283618]"
                    : "text-[#6F5E58]"
                }`}
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {link.label}
              </Link>
            ))}
            <Button
                data-testid="mobile-order-btn"
                onClick={() => { setOrderOpen(true); setMobileOpen(false); }}
                className="rounded-full w-full py-2.5 text-sm font-semibold uppercase tracking-wider mt-2"
                style={{
                  background: "#283618",
                  color: "#FCF8F6",
                  fontFamily: "'Manrope', sans-serif",
                }}
              >
                Order Now
              </Button>
          </div>
        </div>
      )}
      <OrderModal open={orderOpen} onClose={() => setOrderOpen(false)} />
    </header>
  );
}
